import React, { useState } from 'react'
import {
  AddRecipeButton,
  ExperienceIngredientOptions,
  RecipeOptionsIngredients,
} from '@library/recipe-customisations'
import { CollectionUUID, RecipeCoreId, RecipeUUID } from '@library/menu-service'
import { Box, Color, Display, JustifyContent, Spinner } from '@gousto-internal/citrus-react'
import { AlternativeOptions } from '../../../model/alternativeOptions/alternativeOptions'
import { formatSurchargeValue } from '../../../utils/formatSurcharge'

type Props = {
  baseRecipeId: RecipeCoreId
  recipeUuid: RecipeUUID
  menuId: string | null
  currentCollectionId: CollectionUUID
  alternativeOptions: AlternativeOptions
  isLoading: boolean
  reachedLimit: boolean
  onSelectRecipe: (id: RecipeUUID) => void
  addRecipeToBasket: (id: RecipeUUID) => void
  canAddRecipes: boolean
  removeRecipe: () => void
  quantity: number
}

export function IngredientAlternativeOptions({
  baseRecipeId,
  recipeUuid,
  menuId,
  currentCollectionId,
  alternativeOptions,
  isLoading,
  reachedLimit,
  onSelectRecipe,
  addRecipeToBasket,
  canAddRecipes,
  removeRecipe,
  quantity,
}: Props) {
  const [selectedRecipeId, setSelectedRecipeId] = useState<RecipeUUID>(recipeUuid)

  const onChangeSelection = (id: RecipeUUID) => {
    onSelectRecipe(id)
    setSelectedRecipeId(id)
  }

  if (isLoading) {
    return (
      <Box width="100%" display={Display.Flex} justifyContent={JustifyContent.Center} paddingV={5}>
        <Spinner color={Color.ColdGrey_900} size={6} />
      </Box>
    )
  }

  const currentlySelectedRecipe = alternativeOptions?.options?.find(
    (option) => option.recipe.id === selectedRecipeId,
  )

  const surcharge = currentlySelectedRecipe?.surcharge || 0
  const formattedSurcharge = formatSurchargeValue(surcharge)
  const surchargeLabel =
    currentlySelectedRecipe?.surchargeLabel ||
    (formattedSurcharge ? `${formattedSurcharge} per portion` : null)

  const disabled = currentlySelectedRecipe?.isOutOfStock || reachedLimit

  const handleAdd = () => {
    if (!disabled) {
      addRecipeToBasket(selectedRecipeId)
    }
  }

  return (
    <Box>
      <Box width="100%">
        {alternativeOptions.experienceIngredientSlots ? (
          <ExperienceIngredientOptions
            menuId={menuId}
            currentCollectionId={currentCollectionId}
            view="view_recipe_list_screen"
            recipeUuid={recipeUuid}
            selectedRecipeId={selectedRecipeId}
            options={alternativeOptions.options}
            ingredientSlots={alternativeOptions.experienceIngredientSlots}
            onSelectRecipe={onChangeSelection}
          />
        ) : (
          <RecipeOptionsIngredients
            view="view_recipe_list_screen"
            menuId={menuId}
            currentCollectionId={currentCollectionId}
            baseRecipeId={baseRecipeId}
            recipeUuid={selectedRecipeId}
            options={alternativeOptions.options}
            ingredientSlots={alternativeOptions.ingredientSlots}
            onChangeSelection={onChangeSelection}
          />
        )}
      </Box>
      <AddRecipeButton
        canAddRecipes={canAddRecipes}
        isOutOfStock={currentlySelectedRecipe?.isOutOfStock || false}
        isNegativeSurcharge={surcharge < 0}
        onClickAdd={handleAdd}
        onClickRemove={removeRecipe}
        quantity={quantity}
        reachedLimit={reachedLimit}
        surchargeLabel={surchargeLabel}
      />
    </Box>
  )
}
